import { DocPageBanner } from '@squareup/dex-ui';
import React, { FC, PropsWithChildren, useEffect, useState } from 'react';

import styles from './banner.module.css';

interface MdxBannerProps {
  // Light-mode hero illustration.
  imageSrc?: string;
  // Fern/Markdoc alias used by homepage.mdx.
  imagePath?: string;
  // Optional dark-mode hero illustration. When set, we swap to this src
  // whenever the global `dark` class is present on <html> (the
  // ThemeSwitcher convention from `libs/client/shared/ui/src/lib/ThemeSwitcher`).
  darkImageSrc?: string;
  imageAlt?: string;
}

// Watch the `dark` class on <html> so we can swap the banner illustration
// on theme toggle without a reload. SSR / first paint defaults to light;
// the inline script in `_document.tsx` adds `dark` before this component
// mounts when the user's preference is dark, so the post-hydration render
// settles on the right src.
function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    setIsDark(html.classList.contains('dark'));

    const observer = new MutationObserver(() => {
      setIsDark(html.classList.contains('dark'));
    });
    observer.observe(html, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

// Fern's `<Banner imageSrc=… darkImageSrc=… imageAlt=…>body</Banner>` hero
// shows up on the five top-level section landing pages (commerce,
// customers, merchant-details, payments-overview, staff). MDXRemote needs
// a component for the `Banner` tag to render those pages at all — we
// route to dex-ui's existing `DocPageBanner` (same chrome the Contentful
// MarkdownBanner uses) and inject a plain <img> via the `customImage`
// slot so we can (a) reactively swap the src on theme toggle and (b)
// avoid next/Image's remote-host allowlist (the Fern source images live
// on `fern-image-hosting.s3.us-east-1.amazonaws.com`, which isn't in
// `next.config.js`'s `images.remotePatterns`).
const MdxBanner: FC<PropsWithChildren<MdxBannerProps>> = ({
  imageSrc,
  imagePath,
  darkImageSrc,
  imageAlt = '',
  children,
}) => {
  const isDark = useIsDarkMode();
  const resolvedImageSrc = imageSrc ?? imagePath;
  const activeSrc = isDark && darkImageSrc ? darkImageSrc : resolvedImageSrc;
  const useBuiltInImage =
    activeSrc &&
    !darkImageSrc &&
    (activeSrc.startsWith('/docs/_assets/') || activeSrc.startsWith('assets/'));

  if (useBuiltInImage) {
    return (
      <DocPageBanner imagePath={activeSrc} imageAlt={imageAlt}>
        {children}
      </DocPageBanner>
    );
  }

  return (
    <DocPageBanner
      imageAlt={imageAlt}
      customImage={
        activeSrc ? (
          <img
            src={activeSrc}
            alt={imageAlt}
            width={479}
            height={308}
            className={styles.image}
          />
        ) : undefined
      }
    >
      {children}
    </DocPageBanner>
  );
};

MdxBanner.displayName = 'MdxBanner';

export { MdxBanner };
